/**
 * next-brainhealth-checkin — container component on the pulse-page composing
 * the cooldown progress indicator (NextPulseProgress), a pulse-count goal
 * tracker, and the "Take your Pulse" CTA button.
 *
 * Two-section layout:
 *  - Top: light blue card with title, NextPulseProgress, brain illustration
 *  - Bottom: white card with goal title, subtitle, progress bar, and CTA button
 *
 * Milestone system: Users progress through levels at 1, 5, 10, 25, 50, 100, 150, 250, 500.
 * The progress bar shows advancement within the current tier toward the next milestone.
 */
import React from "react";
import styled from "styled-components";

import { Button, StyledP } from "components/simple";
import NextPulseProgress from "components/simple/NextPulseProgress";
import { color, fontFamily, fontSize, spacingDefaults } from "style/constants";
import { ReactComponent as PulseBrain } from "assets/pulse_brain.svg";
import { derivePulseGoalProgress } from "utils/pulseGoalProgress";

export type NextBrainHealthCheckinProps = {
  /** Props forwarded to NextPulseProgress */
  nextPulseDate: string;
  currentDay: number;
  totalDays: number;
  /** Total number of Pulses the user has completed */
  pulsesCompleted: number;
  /** Callback when "Take your Pulse" is tapped */
  onTakePulse: () => void;
};

// --- Top section: light blue card ---

const TopCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacingDefaults.normal};
`;

const TopContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
`;

const TopLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingDefaults.xsmall};
  flex: 1;
`;

const BrainWrapper = styled.div`
  flex-shrink: 0;
  width: 109px;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// --- Bottom section: white card with shadow ---

const BottomCard = styled.div`
  background-color: ${color.WHITE};
  border-radius: 8px;
  padding: ${spacingDefaults.normal};
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacingDefaults.normal};
  box-shadow: 0px 7.365px 3.683px rgba(0, 0, 0, 0.05);
`;

const GoalBarWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 18px;
  border-radius: 184px;
  background-color: ${color.PROGRESSGRAY};
`;

interface GoalBarFillProps {
  percentage: number;
}

const GoalBarFill = styled(
  ({
    percentage,
    ...props
  }: GoalBarFillProps & React.HTMLAttributes<HTMLDivElement>) => (
    <div {...props} />
  )
)<GoalBarFillProps>`
  position: absolute;
  left: 0;
  top: 0;
  width: ${props => props.percentage}%;
  height: 100%;
  border-radius: 184px;
  background-color: ${color.DARKERGRAY};
  transition: width 0.3s ease;
`;

interface GoalBarLabelProps {
  onFill: boolean;
}

const GoalBarLabel = styled(
  ({
    onFill,
    ...props
  }: GoalBarLabelProps & React.HTMLAttributes<HTMLSpanElement>) => (
    <span {...props} />
  )
)<GoalBarLabelProps>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: ${fontFamily.primary};
  font-size: ${fontSize.small};
  font-weight: 600;
  color: ${props => (props.onFill ? color.WHITE : color.NETURALS700)};
  white-space: nowrap;
  pointer-events: none;
`;

// --- Wrapper ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingDefaults.normal};
  width: 100%;
  background-color: ${color.SOFTBLUE};
  border-radius: 8px;
  padding: ${spacingDefaults.normal};
  box-shadow: 0px 4px 2px rgba(0, 0, 0, 0.05);
`;

const NextBrainHealthCheckin: React.FC<NextBrainHealthCheckinProps> = ({
  nextPulseDate,
  currentDay,
  totalDays,
  pulsesCompleted,
  onTakePulse
}) => {
  const cooldownActive = currentDay < totalDays;

  // Derive milestone-based goal progress
  const goalProgress = derivePulseGoalProgress(pulsesCompleted);
  const {
    currentGoal,
    progressInTier,
    tierSize,
    percentage,
    isMaxedOut
  } = goalProgress;

  const goalTitle = isMaxedOut
    ? "All Milestones Complete!"
    : currentGoal === 1
    ? "Complete 1 Pulse"
    : `Complete ${currentGoal} Pulses`;

  const goalSubtitle = isMaxedOut
    ? "You've reached the maximum Pulse milestone"
    : "Unlock a badge when you finish this task";

  // Display format: progressInTier/tierSize for in-progress, or currentGoal/currentGoal when maxed
  const progressLabel = isMaxedOut
    ? `${currentGoal}/${currentGoal}`
    : `${pulsesCompleted}/${currentGoal}`;

  return (
    <Container data-testid="next-brainhealth-checkin">
      <TopCard>
        <TopContent>
          <TopLeft>
            <StyledP
              color={color.PULSE_HEADING}
              fontFamily={fontFamily.primary}
              fontSize="18px"
              fontWeight="700"
            >
              Next Brain Health Check-In
            </StyledP>

            <NextPulseProgress
              nextPulseDate={nextPulseDate}
              currentDay={currentDay}
              totalDays={totalDays}
              showDate={!!nextPulseDate}
            />
          </TopLeft>

          <BrainWrapper>
            <PulseBrain
              style={{ width: "100%", height: "100%" }}
              aria-hidden="true"
            />
          </BrainWrapper>
        </TopContent>
      </TopCard>

      <BottomCard data-testid="next-brainhealth-checkin-goal">
        <div>
          <StyledP
            color={color.BLACK}
            fontFamily={fontFamily.primary}
            fontSize={fontSize.normal}
          >
            {goalTitle}
          </StyledP>
          <StyledP
            color={color.DARKERGRAY}
            fontFamily={fontFamily.primary}
            fontSize={fontSize.xsmall}
          >
            {goalSubtitle}
          </StyledP>
        </div>

        <GoalBarWrapper data-testid="goal-progress-bar">
          {percentage > 0 && (
            <GoalBarFill
              percentage={percentage}
              data-testid="goal-progress-fill"
            />
          )}
          <GoalBarLabel
            onFill={percentage > 50}
            data-testid="goal-progress-label"
          >
            {progressLabel}
          </GoalBarLabel>
        </GoalBarWrapper>

        <Button
          disabled={cooldownActive}
          onClick={cooldownActive ? undefined : onTakePulse}
          data-testid="take-pulse-button"
        >
          Take Your Pulse
        </Button>
      </BottomCard>
    </Container>
  );
};

export default NextBrainHealthCheckin;
